import { useState, useEffect } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Loader2, TrendingUp, TrendingDown, Activity, Users, Zap, FolderOpen, Download } from 'lucide-react'
import {
  ResponsiveContainer,
  ComposedChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts'

const PERIODS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const

const PALETTE = ['#22d3ee', '#a78bfa', '#34d399', '#fbbf24', '#fb7185', '#60a5fa', '#f472b6', '#facc15', '#4ade80', '#c084fc', '#94a3b8', '#67e8f9']

export default function AdminAnalyticsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const [days, setDays] = useState<number>(7)
  const [eventFilter, setEventFilter] = useState<string[]>([])
  const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined)
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined)

  const overview = useQuery(api.analyticsDashboard.overview, isAdmin ? {
    days, eventFilter, channelFilter, sourceFilter,
  } : 'skip')
  const funnel = useQuery(api.analyticsDashboard.funnel, isAdmin ? { days } : 'skip')
  const matrix = useQuery(api.analyticsDashboard.eventMatrix, isAdmin ? { days, top: 8 } : 'skip')
  const catalog = useQuery(api.analyticsDashboard.catalog, isAdmin ? {} : 'skip')
  const recent = useQuery(api.analyticsDashboard.recentEvents, isAdmin ? { limit: 20 } : 'skip')

  // Keyboard shortcuts: 1/2/3/4 switch period.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
      const map: Record<string, number> = { '1': 1, '2': 7, '3': 30, '4': 90 }
      if (map[e.key]) setDays(map[e.key])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!isAdmin) {
    return <div className="p-8 text-sm text-muted-foreground">Admin access required.</div>
  }

  const exportCsv = () => {
    if (!overview) return
    const rows = [['event', 'count', 'unique_users', 'last_seen']]
    for (const e of overview.topEvents) {
      rows.push([e.name, String(e.count), String(e.uniqueUsers), new Date(e.lastSeen).toISOString()])
    }
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-events-${days}d-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-neutral-100">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <Header
          days={days} setDays={setDays}
          channelFilter={channelFilter} setChannelFilter={setChannelFilter}
          sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
          eventFilter={eventFilter} setEventFilter={setEventFilter}
          catalog={catalog?.events ?? []}
          onExport={exportCsv}
        />

        <KPIRow data={overview?.kpis} series={overview?.series} loading={!overview} />

        <Section title="Event volume" subtitle="total events (bars) + unique users (line) over time">
          {!overview ? <Skel /> : <VolumeChart series={overview.series} bucket={overview.bucket} />}
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="lg:col-span-2">
            <Section title="Funnel" subtitle="visitor → signup → project → generation → checkout">
              {!funnel ? <Skel /> : <FunnelChart steps={funnel} />}
            </Section>
          </div>
          <Section title="Client vs server" subtitle="where events originate">
            {!overview ? <Skel /> : <SourceDonut split={overview.sourceSplit} />}
          </Section>
        </div>

        <Section title="Top channels" subtitle="attributed traffic source breakdown" className="mt-4">
          {!overview ? <Skel /> : <ChannelChart channels={overview.channels} />}
        </Section>

        <Section title="Top events by volume" className="mt-4">
          {!overview ? <Skel /> : <TopEventsTable events={overview.topEvents} activeFilter={eventFilter[0]} onPick={(n) => setEventFilter(n ? [n] : [])} />}
        </Section>

        <Section title="Event breakdown over time" subtitle={`top 8 events, stacked, ${matrix?.bucket ?? '…'} buckets`} className="mt-4">
          {!matrix ? <Skel /> : <StackedChart matrix={matrix} />}
        </Section>

        <Section title="Live feed" subtitle="newest 20 events across the system" live className="mt-4">
          {!recent ? <Skel /> : <RecentFeed events={recent} />}
        </Section>
      </div>
    </div>
  )
}

// ── Header / filters ────────────────────────────────────────────────────────

function Header(props: {
  days: number; setDays: (d: number) => void
  channelFilter?: string; setChannelFilter: (s: string | undefined) => void
  sourceFilter?: string; setSourceFilter: (s: string | undefined) => void
  eventFilter: string[]; setEventFilter: (s: string[]) => void
  catalog: string[]
  onExport: () => void
}) {
  return (
    <div className="sticky top-0 z-20 -mx-6 px-6 pt-2 pb-3 mb-5 bg-[#0a0a0b]/85 backdrop-blur-md border-b border-neutral-800/70">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-neutral-500 mt-1">Real-time product telemetry · last <span className="font-mono">{props.days}d</span> <span className="text-neutral-700">· press 1–4 to switch</span></p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={props.onExport}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-neutral-800 bg-neutral-900 text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100 transition-colors"
            title="Export top events as CSV"
          ><Download className="h-3.5 w-3.5" /> CSV</button>
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.label}
                onClick={() => props.setDays(p.days)}
                className={`px-3 py-1.5 text-xs font-mono rounded border transition-colors ${
                  props.days === p.days
                    ? 'bg-cyan-400 text-neutral-950 border-cyan-400'
                    : 'bg-neutral-900 text-neutral-300 border-neutral-800 hover:bg-neutral-800 hover:text-neutral-100'
                }`}
              >{p.label}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <Filter label="event" value={props.eventFilter[0] ?? ''} onChange={(v) => props.setEventFilter(v ? [v] : [])} options={['', ...props.catalog]} />
        <Filter label="channel" value={props.channelFilter ?? ''} onChange={(v) => props.setChannelFilter(v || undefined)} options={['', 'direct', 'organic_search', 'paid_search', 'organic_social', 'paid_social', 'referral', 'email']} />
        <Filter label="source" value={props.sourceFilter ?? ''} onChange={(v) => props.setSourceFilter(v || undefined)} options={['', 'client', 'server']} />
        {(props.eventFilter.length > 0 || props.channelFilter || props.sourceFilter) && (
          <button
            onClick={() => { props.setEventFilter([]); props.setChannelFilter(undefined); props.setSourceFilter(undefined) }}
            className="px-2 py-1 text-cyan-400 hover:text-cyan-300"
          >clear filters</button>
        )}
      </div>
    </div>
  )
}

function Filter({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="flex items-center gap-2 text-neutral-500">
      <span className="uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-neutral-900 border border-neutral-800 text-neutral-200 rounded px-2 py-1 font-mono"
      >
        {options.map((o) => <option key={o} value={o}>{o || `all`}</option>)}
      </select>
    </label>
  )
}

// ── KPI cards ────────────────────────────────────────────────────────────────

function KPIRow({ data, series, loading }: { data?: any; series?: any[]; loading?: boolean }) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-lg bg-neutral-900 border border-neutral-800 animate-pulse" />)}
      </div>
    )
  }
  const spark = (key: string) => (series ?? []).map((d) => d[key] ?? 0)
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <KPI icon={<Activity className="h-4 w-4" />} label="Events" value={data.events} delta={data.eventsDelta} spark={spark('total')} color="#22d3ee" />
      <KPI icon={<Users className="h-4 w-4" />} label="Unique users" value={data.uniqueUsers} delta={data.uniqueUsersDelta} spark={spark('uniqueUsers')} color="#a78bfa" />
      <KPI icon={<FolderOpen className="h-4 w-4" />} label="Projects" value={data.uniqueProjects} delta={data.uniqueProjectsDelta} color="#34d399" />
      <KPI icon={<Zap className="h-4 w-4" />} label="Visitors" value={data.uniqueVisitors} color="#fbbf24" />
    </div>
  )
}

function KPI({ icon, label, value, delta, spark, color }: { icon: React.ReactNode; label: string; value: number; delta?: number | null; spark?: number[]; color: string }) {
  const dir = delta == null ? 'none' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
  return (
    <div className="relative rounded-lg bg-gradient-to-br from-neutral-900 to-neutral-950 border border-neutral-800 p-4 hover:border-neutral-700 transition-colors overflow-hidden">
      <div className="flex items-center justify-between text-neutral-500">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs uppercase tracking-wider">{label}</span>
        </div>
        {dir !== 'none' && (
          <span className={`flex items-center gap-1 text-xs font-mono ${dir === 'up' ? 'text-emerald-400' : dir === 'down' ? 'text-rose-400' : 'text-neutral-500'}`}>
            {dir === 'up' && <TrendingUp className="h-3 w-3" />}
            {dir === 'down' && <TrendingDown className="h-3 w-3" />}
            {delta != null ? `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}%` : ''}
          </span>
        )}
      </div>
      <div className="mt-2 text-3xl font-mono tracking-tight text-neutral-50">
        {formatBigNum(value)}
      </div>
      <div className="text-[10px] text-neutral-600 mt-0.5">{value.toLocaleString()}{delta != null ? ' · vs prev period' : ''}</div>
      {spark && spark.length > 1 && <Sparkline data={spark} color={color} />}
    </div>
  )
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const range = max - min || 1
  const w = 100, h = 24
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - ((v - min) / range) * h
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="absolute bottom-0 left-0 w-full h-8 opacity-60">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

// ── Charts ──────────────────────────────────────────────────────────────────

function Section({ title, subtitle, children, className, live }: { title: string; subtitle?: string; children: React.ReactNode; className?: string; live?: boolean }) {
  return (
    <section className={`rounded-lg bg-gradient-to-br from-neutral-900 to-neutral-950 border border-neutral-800 p-5 transition-colors hover:border-neutral-700/80 ${className ?? ''}`}>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-neutral-100">{title}</h2>
          {live && (
            <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              LIVE
            </span>
          )}
        </div>
        {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  )
}

function Skel() {
  return <div className="h-64 flex items-center justify-center text-neutral-600"><Loader2 className="h-5 w-5 animate-spin" /></div>
}

function VolumeChart({ series, bucket }: { series: any[]; bucket: string }) {
  const fmt = (k: string) => formatBucketLabel(k, bucket)
  // Pass only the keys the chart plots. Leaving `ts` (a ~1.7e12 epoch number)
  // in the data makes recharts fold it into the auto Y-domain, which crushes
  // the real bars to invisible slivers.
  const data = series.map((d) => ({ key: d.key, total: d.total, uniqueUsers: d.uniqueUsers }))
  const maxTotal = Math.max(1, ...data.map((d) => d.total))
  const maxUsers = Math.max(1, ...data.map((d) => d.uniqueUsers))
  return (
    <div className="h-72">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ left: -8, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="evbar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.95} />
              <stop offset="100%" stopColor="#0e7490" stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 4" stroke="#1f1f23" vertical={false} />
          <XAxis dataKey="key" tickFormatter={fmt} stroke="#525252" fontSize={11} tickLine={false} axisLine={false} minTickGap={20} />
          <YAxis yAxisId="left" domain={[0, Math.ceil(maxTotal * 1.1)]} allowDataOverflow stroke="#525252" fontSize={11} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => formatBigNum(v)} />
          <YAxis yAxisId="right" orientation="right" domain={[0, Math.ceil(maxUsers * 1.2)]} allowDataOverflow stroke="#a78bfa" fontSize={11} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => formatBigNum(v)} />
          <Tooltip
            contentStyle={{ backgroundColor: '#0a0a0b', border: '1px solid #262626', borderRadius: 6, fontSize: 12 }}
            labelStyle={{ color: '#a3a3a3' }}
            itemStyle={{ color: '#fafafa' }}
            cursor={{ fill: '#ffffff08' }}
            labelFormatter={(k) => fmt(String(k))}
          />
          <Bar yAxisId="left" dataKey="total" fill="url(#evbar)" radius={[3, 3, 0, 0]} name="events" maxBarSize={56} isAnimationActive={false} />
          <Line yAxisId="right" type="monotone" dataKey="uniqueUsers" stroke="#a78bfa" strokeWidth={2.5} dot={{ r: 2, fill: '#a78bfa' }} name="unique users" isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function SourceDonut({ split }: { split: { source: string; events: number }[] }) {
  const total = split.reduce((s, x) => s + x.events, 0)
  if (total === 0) return <div className="h-64 flex items-center justify-center text-sm text-neutral-500">No events.</div>
  const colors: Record<string, string> = { client: '#22d3ee', server: '#a78bfa' }
  return (
    <div className="h-64 flex flex-col justify-center">
      <div className="w-full h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={split} dataKey="events" nameKey="source" innerRadius={50} outerRadius={75} paddingAngle={3} strokeWidth={0} isAnimationActive={false}>
              {split.map((s) => <Cell key={s.source} fill={colors[s.source] ?? '#737373'} />)}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#0a0a0b', border: '1px solid #262626', borderRadius: 6, fontSize: 12 }} itemStyle={{ color: '#fafafa' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 justify-center text-xs font-mono mt-1">
        {split.map((s) => (
          <span key={s.source} className="flex items-center gap-1.5 text-neutral-300">
            <span className="w-2 h-2 rounded-sm" style={{ background: colors[s.source] ?? '#737373' }} />
            {s.source} {total > 0 ? `${((s.events / total) * 100).toFixed(0)}%` : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

function FunnelChart({ steps }: { steps: { name: string; users: number }[] }) {
  if (steps.length === 0 || steps[0].users === 0) {
    return <div className="text-sm text-neutral-500 h-64 flex items-center justify-center">No funnel data in window.</div>
  }
  const top = steps[0].users
  const overall = top > 0 ? (steps[steps.length - 1].users / top) * 100 : 0
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = top > 0 ? (s.users / top) * 100 : 0
        const prev = i > 0 ? steps[i - 1].users : null
        const conv = prev && prev > 0 ? (s.users / prev) * 100 : null
        return (
          <div key={s.name}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="font-mono text-neutral-300">{s.name}</span>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-neutral-100">{s.users.toLocaleString()}</span>
                <span className="text-neutral-500 font-mono w-12 text-right">{pct.toFixed(1)}%</span>
                {conv != null
                  ? <span className={`font-mono w-20 text-right text-[10px] ${conv >= 50 ? 'text-emerald-400' : conv >= 20 ? 'text-amber-400' : 'text-rose-400'}`}>{conv.toFixed(1)}% step conv</span>
                  : <span className="w-20" />}
              </div>
            </div>
            <div className="h-7 bg-neutral-900 rounded overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500/80 to-cyan-400/40"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )
      })}
      <div className="pt-2 mt-1 border-t border-neutral-800 flex items-baseline justify-between text-xs">
        <span className="text-neutral-500">Overall conversion</span>
        <span className="font-mono text-cyan-300">{overall.toFixed(2)}%</span>
      </div>
    </div>
  )
}

function ChannelChart({ channels }: { channels: { channel: string; events: number; uniqueUsers: number }[] }) {
  const data = channels.slice(0, 8)
  if (data.length === 0) return <div className="text-sm text-neutral-500 h-64 flex items-center justify-center">No channel data.</div>
  const total = data.reduce((s, d) => s + d.events, 0) || 1
  return (
    <div className="space-y-2.5">
      {data.map((d) => {
        const pct = (d.events / total) * 100
        const max = Math.max(...data.map((x) => x.events), 1)
        return (
          <div key={d.channel} className="flex items-center gap-3 text-xs">
            <span className="w-28 shrink-0 font-mono text-neutral-300 text-right">{d.channel}</span>
            <div className="flex-1 h-5 bg-neutral-900 rounded overflow-hidden relative">
              <div className="h-full bg-gradient-to-r from-cyan-500/80 to-cyan-400/40" style={{ width: `${(d.events / max) * 100}%` }} />
              <span className="absolute inset-y-0 right-2 flex items-center font-mono text-[10px] text-neutral-400">{d.uniqueUsers.toLocaleString()} users</span>
            </div>
            <span className="w-24 shrink-0 font-mono text-neutral-100 text-right">{d.events.toLocaleString()}</span>
            <span className="w-12 shrink-0 font-mono text-neutral-500 text-right">{pct.toFixed(1)}%</span>
          </div>
        )
      })}
    </div>
  )
}

function TopEventsTable({ events, onPick, activeFilter }: { events: { name: string; count: number; uniqueUsers: number; lastSeen: number }[]; onPick: (n: string) => void; activeFilter?: string }) {
  const max = Math.max(...events.map((e) => e.count), 1)
  const grandTotal = events.reduce((s, e) => s + e.count, 0) || 1
  return (
    <div className="overflow-hidden">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
          <tr>
            <th className="text-left py-2 font-medium">Event</th>
            <th className="text-right py-2 font-medium">Count</th>
            <th className="text-right py-2 font-medium">% total</th>
            <th className="text-right py-2 font-medium">Users</th>
            <th className="text-right py-2 font-medium pr-3">Last seen</th>
            <th className="text-left py-2 font-medium w-[200px]">Distribution</th>
          </tr>
        </thead>
        <tbody>
          {events.slice(0, 14).map((e) => {
            const active = activeFilter === e.name
            return (
              <tr key={e.name}
                className={`border-b border-neutral-900 cursor-pointer transition-colors ${active ? 'bg-cyan-500/10' : 'hover:bg-neutral-900/50'}`}
                onClick={() => onPick(active ? '' : e.name)}>
                <td className="py-2 font-mono text-xs">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: domainColor(e.name) }} />
                    <span className={active ? 'text-cyan-300' : 'text-neutral-200'}>{e.name}</span>
                  </span>
                  {active && <span className="ml-2 text-[9px] text-cyan-500">filtering ✕</span>}
                </td>
                <td className="py-2 text-right font-mono text-neutral-100">{e.count.toLocaleString()}</td>
                <td className="py-2 text-right font-mono text-[11px] text-neutral-500">{((e.count / grandTotal) * 100).toFixed(1)}%</td>
                <td className="py-2 text-right font-mono text-neutral-400">{e.uniqueUsers.toLocaleString()}</td>
                <td className="py-2 text-right font-mono text-[11px] text-neutral-500 pr-3">{relative(e.lastSeen)}</td>
                <td className="py-2">
                  <div className="h-1.5 bg-neutral-900 rounded">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400/30 rounded" style={{ width: `${(e.count / max) * 100}%` }} />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function StackedChart({ matrix }: { matrix: { topEvents: string[]; series: any[]; bucket: string } }) {
  return (
    <div className="h-72">
      <ResponsiveContainer>
        <BarChart data={matrix.series} stackOffset="none" margin={{ left: -10, right: 8 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#1f1f23" vertical={false} />
          <XAxis dataKey="key" tickFormatter={(k) => formatBucketLabel(k, matrix.bucket)} stroke="#525252" fontSize={11} tickLine={false} axisLine={false} minTickGap={20} />
          <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ backgroundColor: '#0a0a0b', border: '1px solid #262626', borderRadius: 6, fontSize: 12 }}
            labelStyle={{ color: '#a3a3a3' }}
            itemStyle={{ color: '#fafafa' }}
          />
          {matrix.topEvents.map((name, i) => (
            <Bar key={name} dataKey={name} stackId="a" fill={PALETTE[i % PALETTE.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[10px] font-mono">
        {matrix.topEvents.map((name, i) => (
          <span key={name} className="flex items-center gap-1.5 text-neutral-400">
            <span className="w-2 h-2 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
            {name}
          </span>
        ))}
      </div>
    </div>
  )
}

function RecentFeed({ events }: { events: any[] }) {
  const propPreview = (props: any): string => {
    if (!props || typeof props !== 'object') return ''
    const entries = Object.entries(props).filter(([, v]) => v !== undefined && v !== null && v !== '')
    if (entries.length === 0) return ''
    return entries.slice(0, 3).map(([k, v]) => `${k}=${String(v).slice(0, 24)}`).join(' · ')
  }
  return (
    <div className="space-y-0.5 font-mono text-xs">
      {events.map((e) => {
        const row = (
          <>
            <span className="text-neutral-600 w-20 shrink-0">{new Date(e.timestamp).toLocaleTimeString()}</span>
            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${e.source === 'server' ? 'bg-purple-500/15 text-purple-300' : 'bg-cyan-500/15 text-cyan-300'}`}>{e.source}</span>
            <span className="shrink-0 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: domainColor(e.name) }} />
              <span className="text-neutral-100">{e.name}</span>
            </span>
            {e.channel && <span className="text-neutral-500 text-[10px] shrink-0">via {e.channel}</span>}
            <span className="text-neutral-600 text-[10px] truncate">{propPreview(e.props)}</span>
          </>
        )
        return e.workflowId ? (
          <a key={e._id} href={`/admin/projects/detail/${e.workflowId}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-neutral-900 cursor-pointer">
            {row}
          </a>
        ) : (
          <div key={e._id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-neutral-900/50">
            {row}
          </div>
        )
      })}
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatBigNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 10_000) return (n / 1_000).toFixed(1) + 'k'
  if (n >= 1_000) return (n / 1_000).toFixed(2) + 'k'
  return n.toLocaleString()
}

// Stable color per event domain (the part before the first dot), so related
// events read as a family across the table and live feed.
const DOMAIN_COLORS: Record<string, string> = {
  page_view: '#94a3b8',
  signup: '#22d3ee',
  signin: '#67e8f9',
  project: '#34d399',
  clip: '#a78bfa',
  agent: '#c084fc',
  nux: '#fbbf24',
  media: '#f472b6',
  export: '#fb7185',
  video: '#60a5fa',
  checkout: '#4ade80',
  plan: '#facc15',
}
function domainColor(name: string): string {
  const domain = name.split(/[._]/)[0]
  return DOMAIN_COLORS[domain] ?? '#737373'
}

// "2026-05-12" → "May 12"; "2026-05-12T14:00" → "2pm"
function formatBucketLabel(key: string, bucket: string): string {
  if (!key) return ''
  if (bucket === 'hour') {
    const h = parseInt(key.slice(11, 13))
    const ampm = h >= 12 ? 'pm' : 'am'
    const h12 = h % 12 === 0 ? 12 : h % 12
    return `${h12}${ampm}`
  }
  const d = new Date(key + 'T00:00:00Z')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function relative(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`
  return `${Math.round(diff / 86_400_000)}d ago`
}
